<script setup lang="ts">
import { ProductMdw } from '~/lib/services/middleware/middleware.dtos'

const props = defineProps<{
  categoryName: string
  productDetail: ProductMdw
}>()

const { t } = useLocale()
const restaurantStore = useRestaurantStore()
const { staticCategoryRoute } = useMcdRoutes()
const { isEcommerceDisabled } = useAppStore()
const { trackGoEcommerce } = useGTM()

const collapsableTable = {
  title: t('productDetailStatic.nutritional.title').toString(),
}

const collapsableAllergens = {
  title: t('productDetailStatic.allergens.title').toString(),
}

const openChangeRestaurantModal = () => {
  trackGoEcommerce()
  restaurantStore.openChangeRestaurantModal()
}

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Table',
    'about': `${t('productDetailStatic.nutritional.title')} ${props.productDetail.name}`,
  }
})

useJsonld(() => {
  if (!props.productDetail.allergens || !Object.keys(props.productDetail.allergens).length)
    return null
  return {
    '@context': 'https://schema.org',
    '@type': 'Table',
    'about': `${t('productDetailStatic.allergens.title')} ${props.productDetail.name}`,
  }
})
</script>

<template>
  <!-- Navigation backwards to category -->
  <header class="sticky top-0 z-10 w-full bg-white md:hidden">
    <NuxtLink :to="staticCategoryRoute(categoryName)" class="flex w-full flex-row items-center">
      <img src="/images/icons/arrowUp.svg" class="m-4 -rotate-90 cursor-pointer" alt="back" />
      <p class="font-bold">{{ categoryName }}</p>
    </NuxtLink>
  </header>
  <div
    class="mx-auto flex flex-col gap-5 p-5 pb-[9rem] md:grid md:w-[100%] md:grid-cols-[40%_1fr_55%] md:grid-rows-1 md:justify-center md:gap-0 lg:w-[75%]"
  >
    <section class="w-full md:col-span-1">
      <div class="relative flex w-full flex-col md:sticky md:top-[9rem]">
        <ImageResolver
          class="flex h-auto w-9/12 self-center"
          :url="productDetail.image"
          :alt="productDetail.name"
        />

        <div
          v-if="!isEcommerceDisabled"
          class="fixed bottom-0 left-0 z-10 w-full shadow-elevation-up md:relative md:z-0 md:mt-6 md:px-4 md:shadow-none lg:px-10"
        >
          <McdButton
            is-primary
            class="h-16 w-full rounded-none md:h-fit md:rounded"
            @on-click="openChangeRestaurantModal"
          >
            {{ t('common.startOrder') }}
          </McdButton>
        </div>
      </div>
    </section>

    <section class="flex w-full flex-col md:col-start-3 md:col-end-3">
      <h1 class="my-2 text-start text-2xl font-bold md:mt-10 md:text-6xl">
        {{ productDetail.name }}
      </h1>
      <p class="my-4 text-sm text-mcd-secondaryDarkGrey md:text-base">
        {{ productDetail.description }}
      </p>

      <section class="my-4">
        <h2 class="mb-4 text-lg font-bold md:text-3xl">{{ t('productDetailStatic.moreInfo') }}</h2>
        <Collapsable :element="collapsableTable" class="my-4">
          <template #content>
            <NutritionTable :product-detail="productDetail" />
          </template>
        </Collapsable>
        <Collapsable :element="collapsableAllergens" class="my-4">
          <template #content>
            <Allergens :allergens-array="productDetail.allergens" />
          </template>
        </Collapsable>
      </section>
    </section>
  </div>
</template>
